// แถว what-if ของหน้าความจุบัญชี — เริ่มจาก limit จริงของทุกบัญชี แก้ได้ในหน้า (ไม่บันทึก) + เพิ่ม/ลบบัญชีสมมุติ
import { useCallback, useEffect, useRef, useState } from 'react';
import { WINDOW_DEFAULT_SECONDS, WINDOW_MAX_LIMIT, WINDOW_MAX_SECONDS } from '../../../utils/bankLimit';
import { clampInt } from './capacityMath';
import type { AdminBankLite, WhatIfRow } from './types';

export const DAILY_MAX_LIMIT = 1_000_000;

/** ช่วงค่าของแต่ละช่อง (sync กับ validation ฝั่ง backend) */
export const LIMIT_RANGES = {
  n: { min: 0, max: WINDOW_MAX_LIMIT },
  s: { min: 1, max: WINDOW_MAX_SECONDS },
  d: { min: 0, max: DAILY_MAX_LIMIT },
} as const;

const HYPO_DEFAULT = { n: 10, s: 60, d: 0 } as const;

// daily_transaction_limit null → 10000 ตาม gate (checkBankDailyLimit ใช้ ?? 10000) · window ไม่มีคอลัมน์ → ปิด
const fromBank = (b: AdminBankLite): WhatIfRow => ({
  id: `real-${b.id}`,
  kind: 'real',
  bankId: b.id,
  name: b.name,
  bankCode: b.bank_code,
  accountNumber: b.account_number,
  n: Number(b.window_transaction_limit) || 0,
  s: Number(b.limit_window_seconds) || WINDOW_DEFAULT_SECONDS,
  d: Number(b.daily_transaction_limit ?? 10000),
});

export type LimitPatch = Partial<Pick<WhatIfRow, 'n' | 's' | 'd' | 'name'>>;

export const useWhatIfRows = (banks: AdminBankLite[]) => {
  const [rows, setRows] = useState<WhatIfRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const nextHypo = useRef(1);

  // sync จากค่าจริงเฉพาะตอนยังไม่แก้อะไร — เปลี่ยนวัน/รีเฟรชไม่ล้างสิ่งที่กำลังลอง
  useEffect(() => {
    if (!dirty) setRows(banks.map(fromBank));
  }, [banks, dirty]);

  const updateRow = useCallback((id: string, patch: LimitPatch) => {
    setDirty(true);
    setRows((prev) => prev.map((r) => {
      if (r.id !== id) return r;
      const next = { ...r, ...patch };
      if (patch.n !== undefined) next.n = clampInt(patch.n, LIMIT_RANGES.n.min, LIMIT_RANGES.n.max);
      if (patch.s !== undefined) next.s = clampInt(patch.s, LIMIT_RANGES.s.min, LIMIT_RANGES.s.max);
      if (patch.d !== undefined) next.d = clampInt(patch.d, LIMIT_RANGES.d.min, LIMIT_RANGES.d.max);
      return next;
    }));
  }, []);

  const addHypo = useCallback(() => {
    const k = nextHypo.current++;
    setDirty(true);
    setRows((prev) => [...prev, { id: `hypo-${k}`, kind: 'hypo', name: `บัญชีสมมุติ ${k}`, ...HYPO_DEFAULT }]);
  }, []);

  const removeHypo = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id || r.kind !== 'hypo'));
  }, []);

  const resetToReal = useCallback(() => {
    nextHypo.current = 1;
    setDirty(false);
    setRows(banks.map(fromBank));
  }, [banks]);

  return { rows, dirty, updateRow, addHypo, removeHypo, resetToReal };
};
